import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Container } from 'components/base';
import { ScriptMarkupBreadcrumb, getCloudinarySrcSet, getCloudinaryUrl } from 'components/rich';
import useDataContext from 'data/context/data';
import contentApi from 'data/api/content';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { discountLabel, endsSoonLabel, isFreeShippingOnly } from 'ecommerce/lib/coupon';

import Breadcrumb from '../components/breadcrumb';
import BudgetTiles, { BUDGET_TILES } from '../components/gifts/budgetTiles';
import CouponStrip from '../components/gifts/couponStrip';
import FinishingTouches from '../components/gifts/finishingTouches';
import LandingEditorial from '../components/landing/editorial';
import { HERO_IMAGE_SIZES, HeroCollage, HeroFacts } from '../components/landing/hero';
import GiftIdea, { resolveIdeas } from '../components/gifts/giftIdea';
import LandingStory from '../components/landing/story';
import RecipientList from '../components/gifts/recipientList';
import { GIFT_CARD_HREF, GIFT_FACTS } from '../components/gifts/shared';
import { Responsive, SectionHead, ctaTracker, landingImage, productImage } from '../components/landing/shared';
import StickyBar from '../components/landing/stickyBar';
import { Head, Layout } from '../components/layout';
import PdpIcon from '../components/product/pdpIcon';
import useInView from '../lib/useInView';
import useScrolledPast from '../lib/useScrolledPast';
import SETTINGS from '../settings';

// Gifts landing page (docs/gifts-page.md). Explicit page file, so it wins over the category
// route in [...params].js. Title, meta, H1 and the editorial block come from
// content/categories/gifts.md, the rest is design copy kept here like /photo-books. Sections
// shared with /gifts-under-* live in components/gifts/, generic landing blocks in
// components/landing/.
const CATEGORY_ID = 'gifts';
const IDEAS_ID = 'gift-ideas';
const track = ctaTracker('gifts_page');

const HERO_IMAGES = {
  block: productImage('classic-prints/4'),
  couple: productImage('classic-prints/1'),
  tiny: productImage('tiny-square-prints/1'),
};

// Each idea pairs a print with a place to keep it. `products` are priced together from their
// starting prices, the first one is where "Make this gift" goes. `productNames` follow the
// same order, the first name is the idea's link text so the product page gets a descriptive
// anchor.
const IDEAS = [
  {
    id: 'idea-desk',
    large: true,
    recipient: 'For Mom',
    title: 'The desk they’ll smile at every morning',
    body: 'A stack of 20 Square Prints on thick matte paper and an oak block that stands five at a time. Swap the front photo whenever the mood changes.',
    short: '20 Square Prints and an oak block that stands five at a time.',
    productNames: ['Square Prints', 'Wood Photo Block'],
    products: ['square-prints', 'wood-block'],
    image: productImage('classic-prints/4'),
  },
  {
    id: 'idea-brag-book',
    recipient: 'For grandparents',
    title: 'A brag book for the handbag',
    body: 'A 6x6 Softcover Photo Book, small enough to carry everywhere and made for showing off the grandkids.',
    short: 'A 6x6 Softcover Photo Book, small enough to carry everywhere.',
    productNames: ['Softcover Photo Book'],
    products: ['softcover-books'],
    image: landingImage('sc-captions-open'),
  },
  {
    id: 'idea-fridge',
    recipient: 'For the whole family',
    title: 'The fridge, upgraded',
    body: 'Photo Booth Magnets turn a run of favorite frames into a strip that lives where everyone sees it.',
    short: 'A strip of favorite frames that lives where everyone sees it.',
    productNames: ['Photo Booth Magnets'],
    products: ['photobooth-magnets'],
    image: productImage('photobooth-magnet/1'),
  },
  {
    id: 'idea-rope',
    recipient: 'For a new home',
    title: 'A string of good days',
    body: 'A 58 in Photo Rope with eight magnets holds a whole trip of prints along one wall.',
    productNames: ['Photo Rope', 'Square Prints'],
    products: ['photo-ropes', 'square-prints'],
    image: productImage('photo-ropes/1'),
  },
  {
    id: 'idea-year',
    recipient: 'For your person',
    title: 'Your year, bound',
    body: 'An 8x8 Hardcover Photo Book with 26 pages included. Made for anniversaries and the year you just had.',
    short: 'An 8x8 Hardcover Photo Book with 26 pages included.',
    productNames: ['8x8 Hardcover Photo Book'],
    products: ['hardcover-book'],
    image: landingImage('hc-cover-held'),
  },
  {
    id: 'idea-art',
    large: true,
    recipient: 'For the art lover',
    title: 'Ready to hang the minute it’s unwrapped',
    body: 'A Fine Art Print on extra-thick 308 GSM matte paper, held in magnetic oak Wood Poster Rails. No framing needed.',
    short: 'A Fine Art Print in magnetic oak Wood Poster Rails. No framing needed.',
    productNames: ['Fine Art Print', 'Wood Poster Rails'],
    products: ['artprint', 'wooden-rails'],
    image: productImage('artprint/1'),
  },
];

// Recipients jump to the idea that fits them best.
const RECIPIENTS = [
  { name: 'Mom', hint: 'who keeps every card', target: 'idea-desk', image: 'classic-prints/4' },
  {
    name: 'Grandparents',
    hint: 'who always ask for new photos',
    target: 'idea-brag-book',
    image: landingImage('sc-stack-wood'),
  },
  {
    name: 'Your person',
    hint: 'for the camera roll you share',
    target: 'idea-year',
    image: 'classic-prints/1',
  },
  {
    name: 'New parents',
    hint: 'the first year, in a book',
    target: 'idea-year',
    image: landingImage('sc-mom-baby'),
  },
  {
    name: 'Best friend',
    hint: 'the whole trip, in squares',
    target: 'idea-rope',
    image: 'tiny-square-prints/1',
  },
  {
    name: 'Pet people',
    hint: 'a good boy, three feet wide',
    target: 'idea-art',
    image: 'square-engineer-prints/2',
  },
].map((recipient) => ({
  ...recipient,
  image: typeof recipient.image === 'string' ? productImage(recipient.image) : recipient.image,
}));

const BUDGETS = [BUDGET_TILES.under20, BUDGET_TILES.under50, BUDGET_TILES.giftCards];

const GiftsPage = ({ content }) => {
  const dataContext = useDataContext();
  const claimedCoupon = useClaimedCoupon();
  const { previewSummary, summary } = claimedCoupon;
  const heroRef = useRef();
  const ideasEndRef = useRef();
  const heroInView = useInView(heroRef);
  const pastIdeas = useScrolledPast(ideasEndRef);

  const category = dataContext.getCategory(CATEGORY_ID);
  const breadcrumb = [{ name: 'Home', url: '/' }, { name: category.name }];
  const ideas = resolveIdeas(IDEAS, dataContext, previewSummary);
  const [largeA, smallA1, smallA2, smallB1, smallB2, largeB] = ideas;

  const priced = ideas.filter((idea) => idea.price);
  const cheapestIdea = priced.length
    ? priced.reduce((min, idea) => (idea.price.amount < min.price.amount ? idea : min))
    : null;

  // Only when the coupon covers a gift idea (couponPairingSale already skips shipping-only).
  const stickyCoupon =
    ideas.some((idea) => idea.price?.badge) &&
    [`${discountLabel(previewSummary)} with ${previewSummary.code}`, endsSoonLabel(previewSummary)]
      .filter(Boolean)
      .join(' · ');

  const ideaRows = [
    { key: 'a', large: largeA, stack: [smallA1, smallA2] },
    { key: 'b', large: largeB, stack: [smallB1, smallB2], reverse: true },
  ];
  const renderIdea = (idea) => (
    <GiftIdea key={idea.id} idea={idea} onClick={track('idea', 'gift_ideas', idea.id)} />
  );

  return (
    <>
      <Head
        title={category.seoTitle ?? category.name}
        description={category.seoDescription ?? category.shortDescription}
        image={content.seoUrl || getCloudinaryUrl({ imageKey: category.image })}
        heroImage={getCloudinaryUrl({
          imageKey: HERO_IMAGES.block.imageKey,
          prefix: HERO_IMAGES.block.prefix,
        })}
        heroImageSrcSet={getCloudinarySrcSet(HERO_IMAGES.block)}
        heroImageSizes={HERO_IMAGE_SIZES}
      />
      <ScriptMarkupBreadcrumb items={breadcrumb} host={SETTINGS.HOST} />

      <Layout>
        <div className="lnd gft">
          <CouponStrip claimedCoupon={claimedCoupon} />

          <section ref={heroRef} className="lnd-hero">
            <Container className="lnd-hero-inner">
              <div className="lnd-hero-copy">
                <Breadcrumb items={breadcrumb} />
                <h1 className="lnd-h1">{category.heroTitle ?? category.name}</h1>
                <p className="lnd-lead">
                  <Responsive
                    desktop="The best gifts are the ones only you could give. Your photos, printed on thick matte paper, for the people who are in them."
                    phone="The best gifts are the ones only you could give. Your photos, for the people who are in them."
                  />
                </p>
                <div className="lnd-hero-actions">
                  <a href={`#${IDEAS_ID}`} className="lnd-btn" onClick={track('find_idea', 'hero')}>
                    Find a gift idea
                    <PdpIcon name="arrowDown" size={16} />
                  </a>
                  <Anchor
                    href={GIFT_CARD_HREF}
                    className="lnd-btn lnd-btn--outline"
                    onClick={track('gift_card', 'hero')}
                  >
                    <PdpIcon name="mail" size={16} className="lnd-desk" />
                    Send a Gift Card
                    <PdpIcon name="mail" size={16} className="lnd-phone" />
                  </Anchor>
                </div>
                <HeroFacts facts={GIFT_FACTS} />
              </div>

              <HeroCollage
                images={HERO_IMAGES}
                tag={{ to: 'To Grandma', note: ['The whole summer, in squares.', 'Love, Mia'] }}
              />
            </Container>
          </section>

          <RecipientList
            eyebrow="Start with the person"
            title="Who are you shopping for?"
            aside="Think about where they’ll keep it. A fridge, a desk, a handbag, a wall. We’ll take it from there."
            recipients={RECIPIENTS}
            track={track}
          />

          <section id={IDEAS_ID} className="lnd-section gft-ideas">
            <Container>
              <SectionHead
                eyebrow="Gift ideas"
                title="Gift ideas that won’t end up in a drawer"
                aside={
                  <Responsive
                    desktop="Each idea pairs a print with a place to keep it, so it’s on display the day it’s opened."
                    phone="Each one pairs a print with a place to keep it."
                  />
                }
                className="gft-head--ideas"
              />

              {ideaRows.map((row) => (
                <div
                  key={row.key}
                  className={`gft-idea-row${row.reverse ? ' gft-idea-row--reverse' : ''}`}
                >
                  {/* DOM order follows the phone order, the grid places the large idea. */}
                  {row.reverse
                    ? [...row.stack, row.large].map(renderIdea)
                    : [row.large, ...row.stack].map(renderIdea)}
                </div>
              ))}
            </Container>
            <div ref={ideasEndRef} className="gft-sentinel" />
          </section>

          <LandingStory
            image={landingImage('sc-stack-blue')}
            quote="I love that the photo books are small enough to keep in your handbag, because you know grandmas love to show off photos of their grandkids when they’re out and about!"
            author="@littlehiccups"
            product="Softcover Photo Books"
          />

          <BudgetTiles
            title="Something lovely at every price"
            tiles={BUDGETS}
            couponClaimed={Boolean(summary) && !isFreeShippingOnly(summary)}
            track={track}
          />

          <FinishingTouches track={track} />

          <LandingEditorial
            eyebrow="About photo gifts"
            title="Give the gift of the times you’ve already had"
            html={content.extraDescription}
          />

          {pastIdeas ? (
            <StickyBar
              visible={!heroInView}
              title="Short on time?"
              meta="Gift Card, emailed in minutes"
              href={GIFT_CARD_HREF}
              label="Send a Gift Card"
              icon="mail"
              onClick={track('gift_card', 'sticky')}
            />
          ) : (
            <StickyBar
              visible={!heroInView}
              title={cheapestIdea ? `Gift ideas from ${cheapestIdea.price.price}` : 'Gift ideas'}
              meta={stickyCoupon || `${IDEAS.length} ideas · gift note at checkout`}
              sale={Boolean(stickyCoupon)}
              href={`#${IDEAS_ID}`}
              label="Find a gift"
              icon="arrowDown"
              onClick={track('find_idea', 'sticky')}
            />
          )}
        </div>
      </Layout>
    </>
  );
};

GiftsPage.getInitialProps = async () => ({
  content: await contentApi.category(CATEGORY_ID),
});

GiftsPage.propTypes = {
  content: PropTypes.object.isRequired,
};

export default GiftsPage;
